import { ResponsiveTable } from './components/common/ResponsiveTable';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import type { Doctor, TreatmentSpecialty, TreatmentTemplate } from './components/calendar/types';
import { useThemedStyles } from './theme/useThemedStyles';
import './TreatmentTemplatesPanel.css';

interface Props {
  token: string;
  onChanged?: () => void;
}

interface TemplateFormState {
  name: string;
  specialtyId: string;
  suggestedPrice: string;
  suggestedCost: string;
  expectedDurationMinutes: string;
  suggestedAdditionalUseFee: string;
  defaultDoctorId: string;
  doctorIds: number[];
}

interface SpecialtyFormState {
  code: string;
  name: string;
}

const emptyForm: TemplateFormState = {
  name: '',
  specialtyId: '',
  suggestedPrice: '0',
  suggestedCost: '0',
  expectedDurationMinutes: '60',
  suggestedAdditionalUseFee: '0',
  defaultDoctorId: '',
  doctorIds: []
};

const emptySpecialtyForm: SpecialtyFormState = { code: '', name: '' };

export default function TreatmentTemplatesPanel({ token, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const theme = useThemedStyles();
  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: theme.surface,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: 14
  };
  const [templates, setTemplates] = useState<TreatmentTemplate[]>([]);
  const [specialties, setSpecialties] = useState<TreatmentSpecialty[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [specialtyForm, setSpecialtyForm] = useState<SpecialtyFormState>(emptySpecialtyForm);
  const [editingSpecialtyId, setEditingSpecialtyId] = useState<number | null>(null);
  const [savingSpecialty, setSavingSpecialty] = useState(false);
  const [form, setForm] = useState<TemplateFormState>(emptyForm);
  // Kept only while the removed import panel is pruned from this dirty worktree.
  const monthNames: string[] = [];
  const importPreview: { issues: Array<{ severity: string; sourceRow?: number; code: string; message: string }>; [key: string]: any } | null = null;
  const importMonths: string[] = [];
  const importCandidates: Array<{
    action: string;
    normalizedName: string;
    name: string;
    amount: number;
    sourceRowCount: number;
    defaultDoctorId?: number | null;
    defaultDoctorName?: string | null;
    doctors: Array<{ id: number; firstName: string; lastName: string }>;
  }> = [];
  const importBusy: 'preview' | 'commit' | null = null;
  const toggleImportMonth = (_month: string) => {};
  const previewTemplateImport = async () => {};
  const commitTemplateImport = async () => {};
  const updateImportCandidate = (_name: string, _patch: Record<string, unknown>) => {};
  const removeImportCandidate = (_name: string) => {};

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [templatesRes, doctorsRes, specialtiesRes] = await Promise.all([
        fetch(getApiUrl(API_CONFIG.ENDPOINTS.TREATMENT_TEMPLATES), { headers: getAuthHeaders(token) }),
        fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) }),
        fetch(getApiUrl(API_CONFIG.ENDPOINTS.TREATMENT_SPECIALTIES), { headers: getAuthHeaders(token) })
      ]);

      if (!templatesRes.ok) throw new Error(t('treatmentCatalog.loadError', 'Failed to load treatments'));
      if (!doctorsRes.ok) throw new Error('Failed to load doctors');
      if (!specialtiesRes.ok) throw new Error('Failed to load treatment specialties');

      const templatesData = await templatesRes.json();
      const doctorsData = await doctorsRes.json();
      const specialtiesData = await specialtiesRes.json();

      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
      setSpecialties(Array.isArray(specialtiesData) ? specialtiesData : []);
    } catch (err: any) {
      setError(err?.message || t('treatmentCatalog.loadError', 'Failed to load treatments'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // We intentionally reload when token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const canSubmit = useMemo(() => {
    return form.name.trim().length > 0 && !saving;
  }, [form.name, saving]);

  const displayTemplateName = (template: TreatmentTemplate) =>
    i18n.language.startsWith('es') ? template.nameEs || template.name : template.name;
  const displaySpecialtyName = (specialty: TreatmentSpecialty) =>
    i18n.language.startsWith('es') ? specialty.nameEs : specialty.name;
  const sortedDoctors = useMemo(
    () => [...doctors].sort((left, right) =>
      `${left.firstName} ${left.lastName}`.trim().localeCompare(`${right.firstName} ${right.lastName}`.trim(), i18n.language)
    ),
    [doctors, i18n.language]
  );
  const doctorById = useMemo(() => new Map(sortedDoctors.map(doctor => [doctor.id, doctor])), [sortedDoctors]);
  const sortedSpecialties = useMemo(
    () => [...specialties].sort((left, right) => {
      const leftName = i18n.language.startsWith('es') ? left.nameEs : left.name;
      const rightName = i18n.language.startsWith('es') ? right.nameEs : right.name;
      return leftName.localeCompare(rightName, i18n.language);
    }),
    [specialties, i18n.language]
  );
  const visibleTemplates = useMemo(
    () => templates
      .filter(template => !specialtyFilter || String(template.specialtyId || '') === specialtyFilter)
      .sort((left, right) => {
        const leftName = i18n.language.startsWith('es') ? left.nameEs || left.name : left.name;
        const rightName = i18n.language.startsWith('es') ? right.nameEs || right.name : right.name;
        return leftName.localeCompare(rightName, i18n.language);
      }),
    [templates, specialtyFilter, i18n.language]
  );
  const templateGroups = useMemo(() => {
    const groups = new Map<string, { label: string; templates: TreatmentTemplate[] }>();
    visibleTemplates.forEach(template => {
      const key = String(template.specialtyId || 'uncategorized');
      const specialty = template.specialty || specialties.find(item => item.id === template.specialtyId);
      const label = specialty
        ? (i18n.language.startsWith('es') ? specialty.nameEs : specialty.name)
        : t('treatmentCatalog.uncategorized', 'Uncategorized');
      const group = groups.get(key) || { label, templates: [] };
      group.templates.push(template);
      groups.set(key, group);
    });
    return Array.from(groups.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [visibleTemplates, specialties, i18n.language, t]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleToggleDoctor = (doctorId: number) => {
    setForm(prev => {
      const exists = prev.doctorIds.includes(doctorId);
      const nextDoctorIds = exists
        ? prev.doctorIds.filter(id => id !== doctorId)
        : [...prev.doctorIds, doctorId];

      const hasDefault = prev.defaultDoctorId && nextDoctorIds.includes(parseInt(prev.defaultDoctorId, 10));

      return {
        ...prev,
        doctorIds: nextDoctorIds,
        defaultDoctorId: hasDefault ? prev.defaultDoctorId : ''
      };
    });
  };

  const handleEdit = (template: TreatmentTemplate) => {
    const preferredDoctorIds = template.doctors.map(doctor => doctor.id);
    const defaultDoctorId = template.defaultDoctorId && doctorById.has(template.defaultDoctorId)
      ? template.defaultDoctorId
      : null;

    setEditingId(template.id);
    setForm({
      name: template.nameEs || template.name,
      specialtyId: template.specialtyId ? String(template.specialtyId) : '',
      suggestedPrice: String(template.suggestedPrice ?? 0),
      suggestedCost: String(template.suggestedCost ?? 0),
      expectedDurationMinutes: String(template.expectedDurationMinutes ?? 60),
      suggestedAdditionalUseFee: String(template.suggestedAdditionalUseFee ?? 0),
      defaultDoctorId: defaultDoctorId ? String(defaultDoctorId) : '',
      doctorIds: defaultDoctorId && !preferredDoctorIds.includes(defaultDoctorId)
        ? [...preferredDoctorIds, defaultDoctorId]
        : preferredDoctorIds
    });
  };

  const handleDefaultDoctorChange = (value: string) => {
    setForm(prev => {
      if (!value) return { ...prev, defaultDoctorId: '' };
      const doctorId = Number(value);
      return {
        ...prev,
        defaultDoctorId: value,
        doctorIds: prev.doctorIds.includes(doctorId) ? prev.doctorIds : [...prev.doctorIds, doctorId]
      };
    });
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm(t('treatmentCatalog.deleteConfirm', 'Delete this treatment?'))) return;
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.TREATMENT_TEMPLATES}/${templateId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error(t('treatmentCatalog.deleteError', 'Failed to delete treatment'));
      if (editingId === templateId) {
        resetForm();
      }
      await loadData();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || t('treatmentCatalog.deleteError', 'Failed to delete treatment'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload = {
      name: form.name.trim(),
      nameEs: form.name.trim(),
      specialtyId: form.specialtyId ? Number(form.specialtyId) : null,
      suggestedPrice: Number(form.suggestedPrice) || 0,
      suggestedCost: Number(form.suggestedCost) || 0,
      expectedDurationMinutes: Number(form.expectedDurationMinutes) || 60,
      suggestedAdditionalUseFee: Number(form.suggestedAdditionalUseFee) || 0,
      defaultDoctorId: form.defaultDoctorId ? Number(form.defaultDoctorId) : null,
      doctorIds: form.doctorIds
    };

    setSaving(true);
    setError('');

    try {
      const endpoint = editingId
        ? `${API_CONFIG.ENDPOINTS.TREATMENT_TEMPLATES}/${editingId}`
        : API_CONFIG.ENDPOINTS.TREATMENT_TEMPLATES;

      const res = await fetch(getApiUrl(endpoint), {
        method: editingId ? 'PUT' : 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || t('treatmentCatalog.saveError', 'Failed to save treatment'));
      }

      resetForm();
      await loadData();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || t('treatmentCatalog.saveError', 'Failed to save treatment'));
    } finally {
      setSaving(false);
    }
  };

  const resetSpecialtyForm = () => {
    setSpecialtyForm(emptySpecialtyForm);
    setEditingSpecialtyId(null);
  };

  const handleSpecialtySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (savingSpecialty || !specialtyForm.code.trim() || !specialtyForm.name.trim()) return;
    setSavingSpecialty(true);
    setError('');
    try {
      const endpoint = editingSpecialtyId
        ? `${API_CONFIG.ENDPOINTS.TREATMENT_SPECIALTIES}/${editingSpecialtyId}`
        : API_CONFIG.ENDPOINTS.TREATMENT_SPECIALTIES;
      const response = await fetch(getApiUrl(endpoint), {
        method: editingSpecialtyId ? 'PUT' : 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ code: specialtyForm.code.trim(), name: specialtyForm.name.trim(), nameEs: specialtyForm.name.trim() })
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to save treatment specialty');
      resetSpecialtyForm();
      await loadData();
    } catch (err: any) {
      setError(err?.message || 'Failed to save treatment specialty');
    } finally {
      setSavingSpecialty(false);
    }
  };

  const editSpecialty = (specialty: TreatmentSpecialty) => {
    setEditingSpecialtyId(specialty.id);
    setSpecialtyForm({ code: specialty.code, name: specialty.nameEs || specialty.name });
  };

  const deleteSpecialty = async (specialty: TreatmentSpecialty) => {
    if (!window.confirm(`Delete ${specialty.name}?`)) return;
    try {
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.TREATMENT_SPECIALTIES}/${specialty.id}`), {
        method: 'DELETE', headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to delete treatment specialty');
      if (editingSpecialtyId === specialty.id) resetSpecialtyForm();
      await loadData();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete treatment specialty');
    }
  };

  return (
    <section
      className="treatment-catalog-workspace"
      style={{ background: theme.inputBg, border: `1px solid ${theme.border}`, borderRadius: 12 }}
    >
      <div className="treatment-catalog-heading">
        <header className="treatment-catalog-header">
          <div>
            <h2 style={{ margin: 0, color: theme.text, fontSize: 22 }}>{t('treatmentCatalog.title', 'Treatments')}</h2>
            <p style={{ margin: '5px 0 0', color: theme.muted, fontSize: 13, maxWidth: 820 }}>
              {t('treatmentCatalog.description', 'Manage treatments used to auto-fill doctor, duration, cost, suggested price, and additional use fee when creating calendar appointments.')}
            </p>
          </div>
          <div className="treatment-catalog-total" style={{ background: theme.activeBg, color: theme.activeText }}>
            {templates.length} {t('treatmentCatalog.title', 'Treatments').toLocaleLowerCase()}
          </div>
        </header>

        {(loading || error) && (
          <div className="treatment-catalog-status" style={{ color: error ? '#fca5a5' : theme.muted }}>
            {error || t('treatmentCatalog.loading', 'Loading treatments...')}
          </div>
        )}
      </div>

      <div className="treatment-catalog-layout">
        <aside className="treatment-catalog-editor" data-testid="treatment-catalog-editor">
      <section className="treatment-catalog-section" style={{ border: `1px solid ${theme.border}`, background: theme.subtle }}>
        <div>
          <h4 style={{ margin: 0, color: theme.text }}>{t('treatmentCatalog.specialties', 'Specialties')}</h4>
          <div style={{ color: theme.muted, fontSize: 12, marginTop: 3 }}>{t('treatmentCatalog.specialtiesHelp', 'Manage categories used to organize treatments.')}</div>
        </div>
        <form onSubmit={handleSpecialtySubmit} className="treatment-specialty-form">
          <label style={{ display: 'grid', gap: 5, color: theme.muted, fontSize: 12 }}>{t('treatmentCatalog.specialtyCode', 'Code')}<input className="treatment-catalog-control" style={inputStyle} value={specialtyForm.code} onChange={event => setSpecialtyForm(current => ({ ...current, code: event.target.value }))} placeholder="ORTHODONTICS" required /></label>
          <label style={{ display: 'grid', gap: 5, color: theme.muted, fontSize: 12 }}>{t('treatmentCatalog.spanishName', 'Spanish name')}<input className="treatment-catalog-control" style={inputStyle} value={specialtyForm.name} onChange={event => setSpecialtyForm(current => ({ ...current, name: event.target.value }))} required /></label>
          <div className="treatment-specialty-actions">
            <button className="treatment-catalog-button" type="submit" disabled={savingSpecialty} style={{ padding: '10px 12px', border: 0, borderRadius: 6, background: theme.primary, color: theme.primaryText, cursor: savingSpecialty ? 'not-allowed' : 'pointer' }}>{editingSpecialtyId ? t('treatmentCatalog.updateSpecialty', 'Update specialty') : t('treatmentCatalog.createSpecialty', 'Create specialty')}</button>
            <button className="treatment-catalog-button" type="button" onClick={resetSpecialtyForm} style={{ padding: '10px 12px', border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.surface, color: theme.text, cursor: 'pointer' }}>{t('treatmentCatalog.clear', 'Clear')}</button>
          </div>
        </form>
        <div className="treatment-specialty-chips">
          {sortedSpecialties.map(specialty => (
            <div className="responsive-row" key={specialty.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', border: `1px solid ${theme.border}`, borderRadius: 999, color: theme.text, fontSize: 12 }}>
              <span>{displaySpecialtyName(specialty)}</span>
              <button type="button" onClick={() => editSpecialty(specialty)} style={{ border: 0, background: 'transparent', color: theme.primary, cursor: 'pointer' }}>{t('treatmentCatalog.edit', 'Edit')}</button>
              <button type="button" onClick={() => deleteSpecialty(specialty)} style={{ border: 0, background: 'transparent', color: '#f87171', cursor: 'pointer' }}>{t('treatmentCatalog.delete', 'Delete')}</button>
            </div>
          ))}
        </div>
      </section>

      {importPreview && false && <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12, marginBottom: 12, background: theme.subtle, display: 'grid', gap: 12 }}>
        <div className="responsive-row" style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            Google Sheets months
            <div className="responsive-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {monthNames.map(month => (
                <label className="responsive-row"
                  key={month}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    background: importMonths.includes(month) ? theme.activeBg : theme.surface,
                    color: importMonths.includes(month) ? theme.activeText : theme.text,
                    border: `1px solid ${importMonths.includes(month) ? theme.primary : theme.border}`,
                    borderRadius: 6,
                    padding: '6px 8px',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={importMonths.includes(month)}
                    onChange={() => toggleImportMonth(month)}
                    style={{ accentColor: theme.primary }}
                  />
                  {month}
                </label>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={previewTemplateImport}
            disabled={importBusy !== null}
            style={{ padding: '10px 14px', border: 0, borderRadius: 6, background: '#0f766e', color: theme.primaryText, fontWeight: 600, cursor: importBusy ? 'not-allowed' : 'pointer' }}
          >
            {importBusy === 'preview' ? 'Previewing...' : 'Preview Sheets import'}
          </button>
          <button
            type="button"
            onClick={commitTemplateImport}
            disabled={importBusy !== null || importCandidates.filter(candidate => candidate.action === 'create' || candidate.action === 'update').length === 0}
            style={{ padding: '10px 14px', border: 0, borderRadius: 6, background: importCandidates.filter(candidate => candidate.action === 'create' || candidate.action === 'update').length > 0 ? '#2563eb' : '#475569', color: theme.primaryText, fontWeight: 600, cursor: importBusy || importCandidates.filter(candidate => candidate.action === 'create' || candidate.action === 'update').length === 0 ? 'not-allowed' : 'pointer' }}
          >
            {importBusy === 'commit' ? 'Importing...' : 'Import valid templates'}
          </button>
        </div>

        {importPreview && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ color: theme.muted, fontSize: 13 }}>
              {importPreview!.tableName} · {importPreview!.range} · {importPreview!.createCount} create · {importPreview!.updateCount} update · {importPreview!.skipCount} skip · {importPreview!.errorCount} errors · {importPreview!.warningCount} warnings
            </div>
            {importPreview!.issues.length > 0 && (
              <div style={{ maxHeight: 110, overflow: 'auto', display: 'grid', gap: 4 }}>
                {importPreview!.issues.slice(0, 8).map((issue, index) => (
                  <div key={`${issue.code}:${issue.sourceRow}:${index}`} style={{ color: issue.severity === 'error' ? '#fca5a5' : '#fde68a', fontSize: 12 }}>
                    {issue.sourceRow ? `Row ${issue.sourceRow}: ` : ''}{issue.message}
                  </div>
                ))}
                {importPreview!.issues.length > 8 && <div style={{ color: theme.muted, fontSize: 12 }}>+{importPreview!.issues.length - 8} more issues</div>}
              </div>
            )}
            <div className="responsive-table-scroll" style={{ overflowX: 'auto' }}>
              <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                <thead>
                  <tr style={{ color: theme.muted, textAlign: 'left', borderBottom: `1px solid ${theme.border}` }}>
                    <th style={{ padding: 8 }}>Action</th>
                    <th style={{ padding: 8 }}>Template</th>
                    <th style={{ padding: 8 }}>Monto</th>
                    <th style={{ padding: 8 }}>Rows</th>
                    <th style={{ padding: 8 }}>Default doctor</th>
                    <th style={{ padding: 8 }}>Preferred doctors</th>
                    <th style={{ padding: 8 }}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {importCandidates.slice(0, 50).map(candidate => (
                    <tr key={candidate.normalizedName || candidate.name} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: 8, color: candidate.action === 'skip' ? '#fde68a' : '#86efac' }}>{candidate.action}</td>
                      <td style={{ padding: 8, color: theme.text }}>
                        <input
                          style={{ ...inputStyle, minWidth: 180 }}
                          value={candidate.name}
                          onChange={event => updateImportCandidate(candidate.normalizedName, { name: event.target.value })}
                        />
                      </td>
                      <td style={{ padding: 8, color: theme.text }}>
                        <input
                          style={{ ...inputStyle, width: 110 }}
                          type="number"
                          min="0"
                          step="0.01"
                          value={candidate.amount}
                          onChange={event => updateImportCandidate(candidate.normalizedName, { amount: Number(event.target.value) })}
                        />
                      </td>
                      <td style={{ padding: 8, color: theme.text }}>{candidate.sourceRowCount}</td>
                      <td style={{ padding: 8, color: theme.text }}>
                        <select
                          style={{ ...inputStyle, minWidth: 150 }}
                          value={candidate.defaultDoctorId || ''}
                          onChange={event => {
                            const id = event.target.value ? Number(event.target.value) : null;
                            const doctor = candidate.doctors.find(item => item.id === id);
                            updateImportCandidate(candidate.normalizedName, {
                              defaultDoctorId: id,
                              defaultDoctorName: doctor ? `${doctor.firstName} ${doctor.lastName}`.trim() : null
                            });
                          }}
                        >
                          <option value="">-</option>
                          {candidate.doctors.map(doctor => (
                            <option key={doctor.id} value={doctor.id}>{`${doctor.firstName} ${doctor.lastName}`.trim()}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: 8, color: theme.text }}>{candidate.doctors.map(doctor => `${doctor.firstName} ${doctor.lastName}`.trim()).join(', ') || '-'}</td>
                      <td style={{ padding: 8 }}>
                        <button
                          type="button"
                          onClick={() => removeImportCandidate(candidate.normalizedName)}
                          style={{ padding: '7px 9px', border: 0, borderRadius: 6, background: '#991b1b', color: theme.primaryText, cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></ResponsiveTable>
              {importCandidates.length > 50 && <div style={{ color: theme.muted, fontSize: 12, padding: 8 }}>Showing first 50 of {importCandidates.length} candidates.</div>}
            </div>
          </div>
        )}
      </div>}

      <section className="treatment-catalog-section treatment-template-editor" style={{ border: `1px solid ${theme.border}`, background: theme.subtle }}>
        <div>
          <h4 style={{ margin: 0, color: theme.text }}>
            {editingId ? t('treatmentCatalog.updateTreatment', 'Update treatment') : t('treatmentCatalog.createTreatment', 'Create treatment')}
          </h4>
          <div style={{ color: theme.muted, fontSize: 12, marginTop: 3 }}>
            {t('treatmentCatalog.editorHelp', 'Set treatment details and doctor defaults.')}
          </div>
        </div>
      <form onSubmit={handleSubmit} className="treatment-template-form">
        <div className="treatment-template-fields">
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            {t('treatmentCatalog.spanishName', 'Spanish name')}
            <input
              className="treatment-catalog-control"
              style={inputStyle}
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t('treatmentCatalog.spanishName', 'Spanish name')}
              required
            />
          </label>
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            {t('treatmentCatalog.specialty', 'Specialty')}
            <select className="treatment-catalog-control" style={inputStyle} value={form.specialtyId} onChange={(e) => setForm(prev => ({ ...prev, specialtyId: e.target.value }))}>
              <option value="">{t('treatmentCatalog.noSpecialty', 'No specialty')}</option>
              {sortedSpecialties.map(specialty => <option key={specialty.id} value={specialty.id}>{displaySpecialtyName(specialty)}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            Suggested cost
            <input
              className="treatment-catalog-control"
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
              value={form.suggestedCost}
              onChange={(e) => setForm(prev => ({ ...prev, suggestedCost: e.target.value }))}
              placeholder="Cost"
            />
          </label>
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            Suggested price
            <input
              className="treatment-catalog-control"
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
              value={form.suggestedPrice}
              onChange={(e) => setForm(prev => ({ ...prev, suggestedPrice: e.target.value }))}
              placeholder="Price"
            />
          </label>
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            Expected duration (min)
            <input
              className="treatment-catalog-control"
              style={inputStyle}
              type="number"
              min="1"
              step="1"
              value={form.expectedDurationMinutes}
              onChange={(e) => setForm(prev => ({ ...prev, expectedDurationMinutes: e.target.value }))}
              placeholder="Duration (min)"
            />
          </label>
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            Additional use fee
            <input
              className="treatment-catalog-control"
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
              value={form.suggestedAdditionalUseFee}
              onChange={(e) => setForm(prev => ({ ...prev, suggestedAdditionalUseFee: e.target.value }))}
              placeholder="Additional fee"
            />
          </label>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <label style={{ color: theme.muted, fontSize: 13, fontWeight: 600 }}>Allowed doctors</label>
          <div className="treatment-doctor-options">
            {sortedDoctors.map(doctor => {
              const checked = form.doctorIds.includes(doctor.id);
              return (
                <label
                  key={doctor.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    border: `1px solid ${theme.border}`,
                    background: checked ? theme.activeBg : theme.surface,
                    color: checked ? theme.activeText : theme.text,
                    borderRadius: 999,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleDoctor(doctor.id)}
                    style={{ accentColor: theme.primary }}
                  />
                  {doctor.firstName} {doctor.lastName}
                </label>
              );
            })}
          </div>
        </div>

        <div className="treatment-template-footer">
          <label style={{ display: 'grid', gap: 6, color: theme.muted, fontSize: 12, fontWeight: 600 }}>
            Default doctor
            <select
              className="treatment-catalog-control"
              style={inputStyle}
              value={form.defaultDoctorId}
              onChange={(e) => handleDefaultDoctorChange(e.target.value)}
            >
              <option value="">No default doctor</option>
              {sortedDoctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.firstName} {doctor.lastName}
                  </option>
                ))}
            </select>
          </label>

          <button
            className="treatment-catalog-button"
            type="submit"
            disabled={!canSubmit}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '10px 12px',
              background: '#3b82f6',
              color: '#ffffff',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 600
            }}
          >
            {editingId
              ? (saving ? t('treatmentCatalog.updating', 'Updating...') : t('treatmentCatalog.updateTreatment', 'Update treatment'))
              : (saving ? t('treatmentCatalog.saving', 'Saving...') : t('treatmentCatalog.createTreatment', 'Create treatment'))}
          </button>

          <button
            className="treatment-catalog-button"
            type="button"
            onClick={resetForm}
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              padding: '10px 12px',
              background: theme.surface,
              color: theme.muted,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Clear
          </button>
        </div>
      </form>
      </section>
        </aside>

      <main className="treatment-catalog-browser" style={{ border: `1px solid ${theme.border}`, background: theme.surface }}>
        <div className="treatment-catalog-toolbar" data-testid="treatment-catalog-toolbar" style={{ borderBottom: `1px solid ${theme.border}`, background: theme.surface }}>
          <div>
            <h3 style={{ margin: 0, color: theme.text, fontSize: 16 }}>{t('treatmentCatalog.catalog', 'Treatment catalog')}</h3>
            <div style={{ color: theme.muted, fontSize: 12, marginTop: 3 }}>
              {visibleTemplates.length} {t('treatmentCatalog.results', 'results')}
            </div>
          </div>
          <label className="treatment-catalog-filter" style={{ color: theme.muted }}>
            <span>{t('treatmentCatalog.filterSpecialty', 'Filter by specialty')}</span>
            <select className="treatment-catalog-control" style={inputStyle} value={specialtyFilter} onChange={event => setSpecialtyFilter(event.target.value)}>
              <option value="">{t('treatmentCatalog.allSpecialties', 'All specialties')}</option>
              {sortedSpecialties.map(specialty => <option key={specialty.id} value={specialty.id}>{displaySpecialtyName(specialty)}</option>)}
            </select>
          </label>
        </div>
        <div className="treatment-catalog-results" data-testid="treatment-catalog-results">
        {templateGroups.map(group => (
          <section key={group.label} className="treatment-catalog-group">
            <div className="treatment-catalog-group-heading">
              <h4 style={{ margin: 0, color: theme.text, fontSize: 14 }}>{group.label}</h4>
              <span style={{ color: theme.muted }}>{group.templates.length}</span>
            </div>
            <div className="treatment-catalog-cards">
            {group.templates.map(template => (
              <article key={template.id} className="treatment-catalog-card" style={{ border: `1px solid ${theme.border}`, background: theme.subtle }}>
                <div className="treatment-catalog-card-copy">
                  <div style={{ color: theme.text, fontWeight: 700 }}>{displayTemplateName(template)}</div>
                  {template.nameEs && template.nameEs !== template.name && <div style={{ color: theme.muted, fontSize: 12, marginTop: 2 }}>{i18n.language.startsWith('es') ? template.name : template.nameEs}</div>}
                  <div className="treatment-catalog-financials" style={{ color: theme.muted }}>
                    {t('treatmentCatalog.templateFinancials', 'Duration: {{duration}} min | Cost: Bs {{cost}} | Price: Bs {{price}} | Additional fee: Bs {{fee}}', { duration: template.expectedDurationMinutes, cost: Number(template.suggestedCost || 0).toFixed(2), price: Number(template.suggestedPrice || 0).toFixed(2), fee: Number(template.suggestedAdditionalUseFee || 0).toFixed(2) })}
                  </div>
                  <div style={{ color: theme.muted, fontSize: 12, marginTop: 5 }}>{t('treatmentCatalog.doctors', 'Doctors')}: {template.doctors.length > 0 ? template.doctors.map(d => `${d.firstName} ${d.lastName}`).join(', ') : t('treatmentCatalog.none', 'None')}</div>
                  {!!template.defaultDoctorId && doctorById.has(template.defaultDoctorId) && (
                    <div style={{ color: theme.muted, fontSize: 12, marginTop: 3 }}>
                      {t('treatmentCatalog.defaultDoctor', 'Default doctor')}: {doctorById.get(template.defaultDoctorId)?.firstName} {doctorById.get(template.defaultDoctorId)?.lastName}
                    </div>
                  )}
                </div>
                <div className="treatment-catalog-card-actions">
                  <button className="treatment-catalog-button" type="button" onClick={() => handleEdit(template)} style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: '6px 10px', background: theme.surface, color: theme.muted, cursor: 'pointer' }}>{t('treatmentCatalog.edit', 'Edit')}</button>
                  <button className="treatment-catalog-button" type="button" onClick={() => handleDelete(template.id)} style={{ border: '1px solid #7f1d1d', borderRadius: 8, padding: '6px 10px', background: '#450a0a', color: '#fecaca', cursor: 'pointer' }}>{t('treatmentCatalog.delete', 'Delete')}</button>
                </div>
              </article>
            ))}
            </div>
          </section>
        ))}

        {!loading && visibleTemplates.length === 0 && <div style={{ color: theme.muted, fontSize: 13 }}>{t('treatmentCatalog.noTreatments', 'No treatments yet.')}</div>}
        </div>
      </main>
      </div>
    </section>
  );
}

